import { get, map, some, size, omit, keyBy, compact } from "lodash"
import { createSelector } from "reselect"
import { getSecurities, getItems, getBaseComparisonTypes } from "helpers/comparison"
import { getUserPreference } from "helpers/userPreferences"
import { getContentSets } from "selectors/contentSets"
import { Creators as SegmentsCreators } from "modules/compare-segments-root/model"
import { getAllLongShortData } from "modules/compare-segments-root/selectors"
import { getSegmentLabel } from "helpers/segments"
import { getItemActions, getItemInfo } from "modules/experiments/helpers"
import { menuContentSets } from "helpers/menu"
import { getIsOpPro } from "modules/account-root/selectors"
import { getModels as getRawModels } from "resources/models"

const EMPTY_OBJECT = {}

const getAccount = (state) => get(state, "global.selected.account")
export const getCurrentPositionSet = (state) => get(state, "global.data.portfolio.data")
const getCurrentPortfolio = (state) => get(state, "global.data.portfolio")
const getRawSecurities = (state) => get(state, "global.data.securitiesDescriptors.data.model")
const getBenchmarksAndPortfolios = (state) => get(state, "global.data.benchmarksAndPortfolios.data.model")
const getRawExperimentsPreferences = (state) => get(state, "global.data.experimentsPreferences.data")

const getCoverageData = (n) => state => get(state, `global.data.experimentItemCoverage${n}.data.model.simulation.coverage`)

export const getRawComparisonItems = state => get(state, "modules.experiments.comparisonItems")
export const getPortfolioExperiments = state => get(state, "global.data.portfolioExperiments.data.portfolio.experiments")
export const getSavedMetrics = (state) => get(state, "modules.experiments.savedMetrics")
export const getSavedCompositionGroup = (state) => get(state, "modules.experiments.savedCompositionGroup")
export const getSelectedComparisonType = (state) => get(state, "modules.experiments.selectedComparisonType", EMPTY_OBJECT)
export const getExperimentToUpdate = (state) => get(state, "modules.experiments.experimentToUpdate")
export const getExperimentToConvert = (state) => get(state, "modules.experiments.experimentToConvert")

export const getBaseUrl = createSelector(
  [getCurrentPortfolio, getAccount],
  (portfolio, account) => `/${account}/p/${portfolio.id}/experiments`,
)

const getModeledItems = createSelector(
  [getCoverageData(1), getCoverageData(2), getCoverageData(3), getCoverageData(4)],
  (data1, data2, data3, data4) => map([data1, data2, data3, data4], data => some(data, elem => get(elem, "summary.percentGmvAvailable", 0) !== 0)))

export const getSelectedSettings = createSelector(
  [getRawComparisonItems, getSavedMetrics, getSavedCompositionGroup, getSelectedComparisonType],
  (comparisonItems, savedMetrics, savedCompositionGroup, selectedComparisonType) => ({
    comparisonItems: map(comparisonItems, item => omit(item, "uploading")),
    selectedComparisonType,
    savedMetrics,
    savedCompositionGroup,
  }),
)

export const getComparisonPreferences = createSelector(
  [getRawExperimentsPreferences],
  preferences => getUserPreference(preferences, "experimentSettings", {}),
)

export const SEARCH_ITEMS_ACTIONS = {
  benchmark: {
    mainAction: item => () => SegmentsCreators.addComparisonItem(item, null, "experiments-add-comparison"),
  },
  index: {
    mainAction: item => () => SegmentsCreators.addComparisonItem(item, null, "experiments-add-comparison"),
  },
  security: {
    mainAction: item => () => SegmentsCreators.addComparisonItem(item, null, "experiments-add-comparison"),
  },
  portfolio: {
    mainAction: item => () => SegmentsCreators.addComparisonItem(item, null, "experiments-add-comparison"),
  },
  experiment: {
    mainAction: item => () => SegmentsCreators.addComparisonItem(item, null, "experiments-add-comparison"),
  },
}

export const baseGetComparisonItems = createSelector(
  [getRawComparisonItems, getCurrentPositionSet, getRawSecurities, getBenchmarksAndPortfolios, getModeledItems, getPortfolioExperiments],
  getItems,
)

export const getComparisonItems = createSelector(
  [baseGetComparisonItems, getAllLongShortData],
  (comparisonItems, longShortData) => map(comparisonItems, (comparisonItem, i) => ({
    ...comparisonItem,
    label: getSegmentLabel(comparisonItem.segment, comparisonItem),
    actions: getItemActions(comparisonItem, size(comparisonItems), longShortData[i]),
    info: getItemInfo(comparisonItem),
  })),
)

export const getComparisonSecurities = createSelector(
  [baseGetComparisonItems],
  getSecurities,
)

export const getComparisonTypes = createSelector(
  [getContentSets, getIsOpPro],
  (contentSets, isOpPro) => compact([
    ...getBaseComparisonTypes(isOpPro),
    !isOpPro && { id: "frontier", name: "Efficient Frontier", base: true },
    ...menuContentSets(contentSets),
  ]),
)

export const getModels = createSelector(
  [getRawModels],
  (models) => keyBy(models, "id"),
)
